import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ThermometerIcon,
  DropletsIcon,
  WindIcon,
  InfoIcon,
  AlertTriangleIcon,
  BotIcon,
  FlaskConicalIcon,
  XIcon,
  Trash2Icon,
  Building2Icon,
  UserCheckIcon,
  UserMinusIcon,
  LeafIcon,
  SettingsIcon,
  VolumeXIcon } from
'lucide-react';
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: any;
}
import { useTelemetry } from '../hooks/useTelemetry';
import { useAlarms, AgentMessage } from '../hooks/useAlarms';
import { thingsboardService } from '../services/thingsboard';
import { AlarmSoundSettings } from './AlarmSoundSettings';
import { useTranslation } from 'react-i18next';


interface RoomZonesSidebarProps {
  selectedRoom: string;
  onSelectRoom: (id: string) => void;
  rooms: Room[];
  primaryZoneId?: string | null;
  primaryZoneName?: string;
  humidity?: number;
  aqi?: number;
  currentAssetId?: string | null;
  homeName?: string | null;
}
// Removed INITIAL_MESSAGES as we now fetch from ThingsBoard

export function RoomZonesSidebar({
  rooms,
  primaryZoneId = null,
  primaryZoneName,
  humidity,
  aqi,
  currentAssetId = null,
  homeName = null
}: RoomZonesSidebarProps) {
  const { t } = useTranslation();
  const { messages: liveMessages, acknowledge, clear } = useAlarms(currentAssetId);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [showAlarmSettings, setShowAlarmSettings] = useState(false);
  const [alarmSettings, setAlarmSettings] = useState({ enabled: true, src: '', volume: 0.5 });
  const prevMessagesRef = useRef<AgentMessage[]>([]);
  const playingAudiosRef = useRef<Set<HTMLAudioElement>>(new Set());
  const [isAlarmPlaying, setIsAlarmPlaying] = useState(false);

  const fetchAlarmSettings = async () => {
    if (!currentAssetId) return;
    try {
      const attrs = await thingsboardService.getEntityAttributes('ASSET', currentAssetId, 'SERVER_SCOPE', ['selected_sound', 'alarm_sound_enabled', 'sound_amplitude']);
      let enabled = true, src = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3', volume = 0.5;
      attrs.forEach((a: any) => {
        if (a.key === 'alarm_sound_enabled') enabled = String(a.value).toLowerCase() === 'true';
        if (a.key === 'selected_sound' && typeof a.value === 'string' && a.value.trim() !== '') src = a.value;
        if (a.key === 'sound_amplitude') volume = Number(a.value);
      });
      setAlarmSettings({ enabled, src, volume });
    } catch(e) {}
  };

  useEffect(() => {
    fetchAlarmSettings();
  }, [currentAssetId]);

  useEffect(() => {
    // Wait until alarm settings are fully loaded (or fallback applied)
    if (!alarmSettings.src) return;

    const prevIds = new Set(prevMessagesRef.current.map(m => m.id));
    const newLeakageAlarms = messages.filter(
      (m) => m.originalType?.toLowerCase() === 'current leakage alarm' && !prevIds.has(m.id)
    );
    
    // Play sound if there are purely new alarms
    if (newLeakageAlarms.length > 0 && alarmSettings.enabled) {
      console.log('Playing new leakage alarm loop:', alarmSettings.src, newLeakageAlarms);
      const audio = new Audio(alarmSettings.src);
      audio.volume = alarmSettings.volume;
      
      playingAudiosRef.current.add(audio);
      setIsAlarmPlaying(true);

      let count = 0;
      const maxPlays = 10;
      
      const onEnded = () => {
        count++;
        if (count < maxPlays) {
          audio.currentTime = 0;
          audio.play().catch(e => console.error('Failed to play alarm sound loop', e));
        } else {
          playingAudiosRef.current.delete(audio);
          if (playingAudiosRef.current.size === 0) setIsAlarmPlaying(false);
        }
      };
      
      audio.addEventListener('ended', onEnded);
      
      audio.play().catch(e => {
        console.error('Failed to play alarm sound', e);
        playingAudiosRef.current.delete(audio);
        if (playingAudiosRef.current.size === 0) setIsAlarmPlaying(false);
      });
    }
    
    prevMessagesRef.current = messages;
  }, [messages, alarmSettings]);

  const handleStopAlarmSound = () => {
    playingAudiosRef.current.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    playingAudiosRef.current.clear();
    setIsAlarmPlaying(false);
  };

  const handleCloseAlarmSettings = () => {
    setShowAlarmSettings(false);
    fetchAlarmSettings();
  };

  // Sync with live messages
  useEffect(() => {
    setMessages(liveMessages);
  }, [liveMessages]);

  const [selectedMsg, setSelectedMsg] = useState<AgentMessage | null>(null);

const ZONE_TELEMETRY_KEYS = ['temperature', 'humidity', 'dust', 'TVOCs', 'CO2'];
const APARTMENT_TELEMETRY_KEYS = ['presence_state'];

  const { data: zoneData } = useTelemetry(
    primaryZoneId,
    ZONE_TELEMETRY_KEYS,
    'ASSET'
  );

  const { data: apartmentData } = useTelemetry(
    currentAssetId || null,
    APARTMENT_TELEMETRY_KEYS,
    'ASSET'
  );
  const handleCardClick = (msg: AgentMessage) => {
    if (!msg.isRead) {
      acknowledge(msg.id);
    }
    setSelectedMsg(msg);
  };
  const handleClose = () => {
    setSelectedMsg(null);
  };
  const handleDelete = (msgId: string) => {
    clear(msgId);
    setSelectedMsg(null);
    handleStopAlarmSound();
  };
  // Extract values from ASSET telemetry or fallback to defaults
  const avgTemp = zoneData['temperature']?.[0]?.value ? Number(zoneData['temperature'][0].value).toFixed(1) : '-';
  const currentHumidity = zoneData['humidity']?.[0]?.value ? Number(zoneData['humidity'][0].value).toFixed(0) : '-';
  const currentDust = zoneData['dust']?.[0]?.value ? Number(zoneData['dust'][0].value) : '-';
  const currentTvoc = zoneData['TVOCs']?.[0]?.value ? Number(zoneData['TVOCs'][0].value) : '-';
  const currentCo2  = zoneData['CO2']?.[0]?.value ? Number(zoneData['CO2'][0].value) : '-';

  // Presence State (Apartment)
  const presenceValue = apartmentData['presence_state']?.[0]?.value as string | boolean | undefined;
  const isPresent = Number(presenceValue) > 0;

  const [lastPresenceTs, setLastPresenceTs] = useState<number | null>(null);
  const [timeNow, setTimeNow] = useState(Date.now());

  useEffect(() => {
    if (isPresent) return;
    const interval = setInterval(() => setTimeNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, [isPresent]);

  useEffect(() => {
    const pData = apartmentData['presence_state'] || [];
    const activePoint = pData.find((d: any) => Number(d.value) > 0);
    if (activePoint && (!lastPresenceTs || activePoint.ts > lastPresenceTs)) {
      setLastPresenceTs(activePoint.ts);
    }
  }, [apartmentData, lastPresenceTs]);

  useEffect(() => {
    if (currentAssetId && presenceValue !== undefined && !isPresent && !lastPresenceTs) {
      const endTs = Date.now();
      const startTs = endTs - 7 * 24 * 60 * 60 * 1000;
      thingsboardService.getTimeseries('ASSET', currentAssetId, 'presence_state', startTs, endTs)
        .then((res: any) => {
          const arr = res['presence_state'] || [];
          const activePoint = arr.find((d: any) => Number(d.value) > 0);
          if (activePoint) setLastPresenceTs(activePoint.ts);
        }).catch(err => console.warn('Failed to fetch historical presence', err));
    }
  }, [currentAssetId, presenceValue, isPresent, lastPresenceTs]);

  let lastActiveText = t('sidebar.no_record');
  if (lastPresenceTs) {
    const diffMins = Math.floor((timeNow - lastPresenceTs) / 60000);
    if (diffMins < 1) lastActiveText = t('sidebar.just_now');
    else if (diffMins < 60) lastActiveText = t('sidebar.mins_ago', { val: diffMins });
    else if (diffMins < 1440) lastActiveText = t('sidebar.hours_mins_ago', { hours: Math.floor(diffMins / 60), mins: diffMins % 60 });
    else lastActiveText = t('sidebar.days_ago', { val: Math.floor(diffMins / 1440) });
  }

  // AQI (Dust) status
  const aqiStatus = currentDust === '-' ? '' : currentDust <= 50 ? t('sidebar.aqi_good') : currentDust <= 100 ? t('sidebar.aqi_fair') : t('sidebar.aqi_bad');
  const aqiColor = currentDust === '-' ? 'text-slate-500' : currentDust <= 50 ? 'text-emerald-400' : currentDust <= 100 ? 'text-amber-400' : 'text-red-400';
  const aqiBg = currentDust === '-' ? 'bg-slate-500' : currentDust <= 50 ? 'bg-emerald-400' : currentDust <= 100 ? 'bg-amber-400' : 'bg-red-400';

  // Humidity status
  const parsedHumidity = Number(currentHumidity);
  const humidityOk = (parsedHumidity >= 30 && parsedHumidity <= 60);
  const humidityColor = humidityOk ? 'text-cyan-400' : 'text-amber-400';

  // TVOC status
  const tvocStatus = currentTvoc === '-' ? '' :  currentTvoc < 200 ? t('sidebar.tvoc_good') : currentTvoc < 400 ? t('sidebar.tvoc_fair') : t('sidebar.tvoc_bad');
  const tvocColor = currentTvoc === '-' ? 'text-slate-500' : currentTvoc < 200 ? 'text-emerald-400' : currentTvoc < 400 ? 'text-amber-400' : 'text-red-400';
  const tvocBg = currentTvoc === '-' ? 'bg-slate-500' : currentTvoc < 200 ? 'bg-emerald-400' : currentTvoc < 400 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <>
      <div className="w-[30%] flex-shrink-0 flex flex-col gap-2 p-3 border-r border-white/20 bg-[#0a0f1a]/30 h-full overflow-hidden">
        
          {/* Apartment & Presence */}
          <motion.div
            initial={{
              opacity: 0,
              y: 10
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              delay: 0.05
            }}
            className="px-3 py-3 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5 flex flex-col justify-between"
            style={{
              minHeight: '5rem'
            }}>

            <div className="flex items-center gap-1.5">
              <Building2Icon className="w-5 h-5 text-slate-500 flex-shrink-0" />
              <span className="text-[1.05rem] font-medium text-white text-slate-300 tracking-tight flex-1 min-w-0 truncate">
                {homeName}
              </span>
              <div className="flex items-center gap-1.5 flex-shrink-0 bg-black/20 px-2 py-1 rounded-md border border-white/5">
                {isPresent ? (
                  <>
                    <UserCheckIcon className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-[0.75rem] font-bold text-emerald-400">
                      {t('sidebar.presence')}
                    </span>
                  </>
                ) : (
                  <>
                    <UserMinusIcon className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-[0.75rem] font-bold text-slate-500">
                      {t('sidebar.absence')}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="text-[0.65rem] text-slate-600 font-roboto text-right">
              {isPresent ? '' : `${t('sidebar.last_detected')}${lastActiveText}`}
            </div>
          </motion.div>

        {/* Divider */}
        <div className="h-px bg-white/5 my-0.5" />

        {/* SED Agent Card */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5 flex flex-col gap-2 flex-1 min-h-0 relative overflow-hidden mb-1">
          {/* Background Image when empty */}
          {/* {messages.length === 0 && ( */}
            <div className="absolute -bottom-6 -right-6 w-64 h-64 opacity-20 pointer-events-none">
              <img src="/modern_house.png" alt="Modern House Background" className="w-full h-full object-contain" style={{ maskImage: 'radial-gradient(circle at bottom right, black 50%, transparent 80%)', WebkitMaskImage: 'radial-gradient(circle at bottom right, black 50%, transparent 80%)' }} />
            </div>
          {/* )} */}

          {/* SED Agent Header */}
          <div className="flex items-center gap-2 relative z-10">
            <BotIcon className="w-4 h-4 text-cyan-400" />
            <span className="text-[1.0rem] text-white font-roboto font-bold uppercase tracking-widest text-slate-200">
              {t('sidebar.agent_name')}
            </span>
            <span className="ml-auto flex items-center gap-1.5">
              {isAlarmPlaying && (
                <button
                  onClick={handleStopAlarmSound}
                  className="p-1 rounded-md bg-red-500/20 text-red-400 hover:bg-red-500/40 transition-colors animate-pulse"
                  title={t('sidebar.stop_alarm_sound')}
                >
                  <VolumeXIcon className="w-3.5 h-3.5" />
                </button>
              )}
              <span className="text-[0.7rem] font-roboto text-cyan-400/60 bg-cyan-400/10 px-1.5 py-0.5 rounded-full border border-cyan-400/20">
                {t('sidebar.count_unit', { val: messages.length })}
              </span>
              <button
                onClick={() => setShowAlarmSettings(true)}
                className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                title={t('sidebar.alarm_sound_settings')}
              >
                <SettingsIcon className="w-3.5 h-3.5" />
              </button>
            </span>
          </div>

          {/* Agent Message Cards - Scrollable */}
          <div className="flex flex-col space-y-2 overflow-y-auto flex-1 min-h-0 relative z-10 custom-scrollbar pr-1">
            {messages.length > 0 ? (
              <AnimatePresence>
                {messages.map((msg, index) => {
                  const isWarning = msg.type === 'warning';
                  return (
                    <motion.div
                      key={msg.id}
                      layout
                      initial={{
                        opacity: 0,
                        y: 10
                      }}
                      animate={{
                        opacity: 1,
                        y: 0
                      }}
                      exit={{
                        opacity: 0,
                        scale: 0.95
                      }}
                      transition={{
                        duration: 0.2
                      }}
                      onClick={() => handleCardClick(msg)}
                      className={`
                        group relative flex items-start gap-2.5 p-2.5 rounded-xl border transition-all duration-300 w-full text-left overflow-hidden cursor-pointer hover:bg-white/[0.04] shrink-0 min-h-[3.2rem]
                        ${isWarning ? 'bg-amber-500/5 border-amber-500/30' : 'bg-white/[0.02] border-white/10'}
                      `}>
                      
                      {/* Left accent bar */}
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-0.5 ${isWarning ? 'bg-amber-400' : 'bg-cyan-400/40'}`} />
                      

                      {/* Icon */}
                      <div
                        className={`p-1.5 rounded-lg flex-shrink-0 ${isWarning ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/10 text-cyan-400'}`}>
                        
                        {isWarning ?
                        <AlertTriangleIcon className="w-3.5 h-3.5" /> :

                        <InfoIcon className="w-3.5 h-3.5" />
                        }
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pt-0.5">
                        <div
                          className={`text-[0.85rem] font-medium leading-snug mb-0.5 ${isWarning ? 'text-amber-200' : 'text-slate-200'}`}>
                          
                          {msg.title}
                        </div>
                        <div className="text-[0.7rem] text-slate-400 leading-relaxed line-clamp-2">
                          {msg.description}
                        </div>
                      </div>
                    </motion.div>);

                })}
              </AnimatePresence>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center opacity-60">
                <BotIcon className="w-8 h-8 text-cyan-400/50 mb-2" />
                <span className="text-xs text-slate-400 font-medium tracking-wide">
                  {t('sidebar.no_new_notifications') || '새로운 알림이 없습니다'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Divider - Messages / Sensor Cards */}
        <div className="h-px bg-white/10 my-0.5" />

        <div className="flex items-center gap-2 px-1">
          <LeafIcon className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[1rem] text-white font-roboto font-bold uppercase tracking-widest text-slate-500">
            {t('sidebar.indoor_env')}
          </span>
          <span className="ml-auto text-[0.7rem] font-roboto text-cyan-400/60 bg-cyan-400/10 px-1.5 py-0.5 rounded-full border border-cyan-400/20">
            {primaryZoneName || t('rooms.living')}
          </span>
        </div>

        {/* Sensor Status Cards */}
        <div className="flex flex-col gap-2">
          {/* Avg Temp */}
          <motion.div
            initial={{
              opacity: 0,
              y: 10
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              delay: 0.1
            }}
            className="p-2.5 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5">

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <ThermometerIcon className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[0.9rem] font-roboto text-slate-400 uppercase tracking-wider">
                  {t('sidebar.indoor_temp')}
                </span>
              </div>
              <span className="text-base font-roboto font-bold text-white glow-text-amber">
                {avgTemp}°C
              </span>
            </div>
            <div className="h-1 mt-1.5" />
            <div className="mt-0.5 text-[0.65rem] text-slate-600 text-right font-roboto">
              {t('sidebar.temp_optimal_range')}
            </div>
          </motion.div>

          {/* Humidity */}
          <motion.div
            initial={{
              opacity: 0,
              y: 10
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              delay: 0.2
            }}
            className="p-2.5 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5">

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <DropletsIcon className={`w-3.5 h-3.5 ${humidityColor}`} />
                <span className="text-[0.9rem] font-roboto text-slate-400 uppercase tracking-wider">
                  {t('sidebar.indoor_humidity')}
                </span>
              </div>
              <span
                className={`text-base font-roboto font-bold ${humidityColor}`}>

                {currentHumidity}%
              </span>
            </div>
            <div className="h-1 mt-1.5" />
            <div className="mt-0.5 text-[0.65rem] text-slate-600 text-right font-roboto">
              {humidityOk ? t('sidebar.humidity_optimal') : t('sidebar.humidity_adjusting')}
            </div>
          </motion.div>

          {/* Air Quality */}
          <motion.div
            initial={{
              opacity: 0,
              y: 10
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              delay: 0.3
            }}
            className="p-2.5 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5">

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <WindIcon className={`w-3.5 h-3.5 ${aqiColor}`} />
                <span className="text-[0.9rem] font-roboto text-slate-400 uppercase tracking-wider">
                  {t('sidebar.air_quality')}
                </span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className={`text-base font-roboto font-bold ${aqiColor}`}>
                  {currentDust}
                </span>
                <span className={`text-[0.65rem] text-slate-500 font-roboto ${aqiColor}`}>
                  µg/m³
                </span>
                <span
                  className={`text-[0.5rem] font-bold uppercase tracking-wider ${aqiColor} opacity-80`}>

                  {aqiStatus}
                </span>
              </div>
            </div>
            <div className="h-1 mt-1.5" />
            <div className="flex justify-between mt-0.5 text-[0.65rem] text-slate-600 font-roboto">
              <span>PM2.5: {currentDust}  µg/m³</span>
              <span>CO2: {currentCo2} ppm</span>
            </div>
          </motion.div>

          {/* TVOC */}
          <motion.div
            initial={{
              opacity: 0,
              y: 10
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              delay: 0.4
            }}
            className="p-2.5 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5">

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FlaskConicalIcon className={`w-3.5 h-3.5 ${tvocColor}`} />
                <span className="text-[0.9rem] font-roboto text-slate-400 uppercase tracking-wider">
                  TVOC
                </span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className={`text-base font-roboto font-bold ${tvocColor}`}>
                  {currentTvoc}
                </span>
                <span className={`text-[0.65rem] text-slate-500 font-roboto ${tvocColor}`}>
                  ppb
                </span>
                <span
                  className={`text-[0.65rem] font-bold uppercase tracking-wider ${tvocColor} opacity-80`}>

                  {tvocStatus}
                </span>
              </div>
            </div>
            <div className="h-1 mt-1.5" />
            <div className="flex justify-between mt-0.5 text-[0.65rem] text-slate-600 font-roboto">
              <span>{t('sidebar.tvoc_desc')}</span>
              <span>{t('sidebar.tvoc_ref')}</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Toast Modal Overlay - rendered as portal-like fixed overlay */}
      <AnimatePresence>
        {selectedMsg &&
        <motion.div
          initial={{
            opacity: 0
          }}
          animate={{
            opacity: 1
          }}
          exit={{
            opacity: 0
          }}
          transition={{
            duration: 0.2
          }}
          className="fixed inset-0 z-[200] flex items-center justify-center"
          onClick={handleClose}>

            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

            {/* Toast Card */}
            <motion.div
            initial={{
              opacity: 0,
              scale: 0.9,
              y: 20
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }}
            exit={{
              opacity: 0,
              scale: 0.9,
              y: 20
            }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30
            }}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 w-[90%] max-w-lg mx-auto">

              <div
              className={`
                glass-panel rounded-2xl p-6 border-l-4 shadow-2xl
                ${selectedMsg.type === 'warning' ? 'border-l-amber-400' : 'border-l-cyan-400'}
              `}>

                {/* Header */}
                <div className="flex items-start gap-3 mb-4">
                  <div
                  className={`p-2 rounded-lg flex-shrink-0 ${selectedMsg.type === 'warning' ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/15 text-cyan-400'}`}>

                    {selectedMsg.type === 'warning' ?
                  <AlertTriangleIcon className="w-5 h-5" /> :

                  <InfoIcon className="w-5 h-5" />
                  }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                    className={`text-base font-bold leading-snug ${selectedMsg.type === 'warning' ? 'text-amber-200' : 'text-white'}`}>

                      {selectedMsg.title}
                    </div>
                    <div className="text-[0.8rem] font-roboto text-slate-500 uppercase tracking-wider mt-1">
                      {selectedMsg.type === 'warning' ? t('sidebar.warning_label') : t('sidebar.info_label')} ·
                      {t('sidebar.agent_name')}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="text-sm text-slate-300 leading-relaxed mb-6 pl-1">
                  {selectedMsg.description}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 justify-end">
                  <button
                  onClick={() => handleDelete(selectedMsg.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium hover:bg-red-500/20 transition-all active:scale-95">

                    <Trash2Icon className="w-3.5 h-3.5" />
                    {t('common.delete')}
                  </button>
                  <button
                  onClick={handleClose}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 text-sm font-medium hover:bg-white/10 transition-all active:scale-95">

                    <XIcon className="w-3.5 h-3.5" />
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        }
      </AnimatePresence>

      <AnimatePresence>
        {showAlarmSettings && (
          <AlarmSoundSettings
            currentAssetId={currentAssetId}
            onClose={handleCloseAlarmSettings}
          />
        )}
      </AnimatePresence>
    </>
  );
}